import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Company {
  id: string;
  name: string;
  plan: string;
  branche: string | null;
  trial_ends_at: string | null;
}

interface ImpersonationContextType {
  impersonatedCompanyId: string | null;
  impersonatedCompany: Company | null;
  setImpersonatedCompany: (companyId: string | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
  allCompanies: Company[];
  isLoadingCompanies: boolean;
  isLoading: boolean; // Gesamt-Loading-State
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { profile, loading: authLoading } = useAuth();
  const [impersonatedCompanyId, setImpersonatedCompanyId] = useState<string | null>(null);

  // Während Auth lädt, leeren Context mit isLoading=true liefern
  const isSuperAdmin = !authLoading && profile?.is_superadmin === true;

  // Nur SuperAdmins können alle Companies sehen
  const { data: allCompanies = [], isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["allCompanies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, plan, branche, trial_ends_at")
        .order("name");

      if (error) {
        console.error("Error loading companies:", error);
        return [];
      }
      return data as Company[];
    },
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Impersonierte Company laden
  const { data: impersonatedCompany } = useQuery({
    queryKey: ["impersonatedCompany", impersonatedCompanyId],
    queryFn: async () => {
      if (!impersonatedCompanyId) return null;

      const found = allCompanies.find((c) => c.id === impersonatedCompanyId);
      return found ?? null;
    },
    enabled: !!impersonatedCompanyId && allCompanies.length > 0,
  });

  // Audit-Log für Impersonation-Events
  const logImpersonationEvent = useCallback(async (action: 'start' | 'end', companyId: string) => {
    if (!profile?.id) return;
    
    try {
      await supabase.from("impersonation_audit_log").insert({
        superadmin_id: profile.id,
        target_company_id: companyId,
        action,
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error("Failed to log impersonation event:", error);
    }
  }, [profile?.id]);

  const setImpersonatedCompany = useCallback(async (companyId: string | null) => {
    if (!isSuperAdmin) return;
    
    // Log end of previous impersonation
    if (impersonatedCompanyId && companyId !== impersonatedCompanyId) {
      await logImpersonationEvent('end', impersonatedCompanyId);
    }
    
    // Eigene Company = keine Impersonation
    if (companyId === profile?.company_id) {
      setImpersonatedCompanyId(null);
    } else if (companyId) {
      // Log start of new impersonation
      await logImpersonationEvent('start', companyId);
      setImpersonatedCompanyId(companyId);
    } else {
      setImpersonatedCompanyId(null);
    }
  }, [isSuperAdmin, profile?.company_id, impersonatedCompanyId, logImpersonationEvent]);

  const clearImpersonation = useCallback(async () => {
    if (impersonatedCompanyId) {
      await logImpersonationEvent('end', impersonatedCompanyId);
    }
    setImpersonatedCompanyId(null);
  }, [impersonatedCompanyId, logImpersonationEvent]);

  const isImpersonating = impersonatedCompanyId !== null;
  
  // Gesamt-Loading: Auth lädt ODER (SuperAdmin und Companies laden noch)
  const isLoading = authLoading || (isSuperAdmin && isLoadingCompanies);

  // Während Auth lädt, leeren Context mit Loading bereitstellen
  if (authLoading) {
    return (
      <ImpersonationContext.Provider
        value={{
          impersonatedCompanyId: null,
          impersonatedCompany: null,
          setImpersonatedCompany: () => {},
          clearImpersonation: () => {},
          isImpersonating: false,
          allCompanies: [],
          isLoadingCompanies: true,
          isLoading: true,
        }}
      >
        {children}
      </ImpersonationContext.Provider>
    );
  }

  // Wenn kein SuperAdmin, liefere einen leeren Context
  if (!isSuperAdmin) {
    return (
      <ImpersonationContext.Provider
        value={{
          impersonatedCompanyId: null,
          impersonatedCompany: null,
          setImpersonatedCompany: () => {},
          clearImpersonation: () => {},
          isImpersonating: false,
          allCompanies: [],
          isLoadingCompanies: false,
          isLoading: false,
        }}
      >
        {children}
      </ImpersonationContext.Provider>
    );
  }

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedCompanyId,
        impersonatedCompany: impersonatedCompany ?? null,
        setImpersonatedCompany,
        clearImpersonation,
        isImpersonating,
        allCompanies,
        isLoadingCompanies,
        isLoading,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    // Return safe defaults instead of throwing - prevents crashes outside provider
    console.warn("[useImpersonation] Used outside ImpersonationProvider, returning defaults");
    return {
      impersonatedCompanyId: null,
      impersonatedCompany: null,
      setImpersonatedCompany: () => {
        console.warn("[useImpersonation] setImpersonatedCompany called outside ImpersonationProvider");
      },
      clearImpersonation: () => {
        console.warn("[useImpersonation] clearImpersonation called outside ImpersonationProvider");
      },
      isImpersonating: false,
      allCompanies: [],
      isLoadingCompanies: false,
      isLoading: false,
    };
  }
  return context;
}
